import { CommonModule, KeyValue } from '@angular/common';
import { afterRenderEffect, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { NgxMaskDirective } from 'ngx-mask';
import { FormAsType } from '../../../common/form-converter';
import { EnrollmentDraft, EnrollmentType, MasterClientProductViewDto } from '../../../models/api-data-contracts';
import { Relationships, States } from '../../../common/constants';
import { BehaviorSubject, of } from 'rxjs';
import { DateMaskDirective } from '../../../directives/date-mask.directive';
import { PlansSummaryComponent } from '../plans-summary/plans-summary.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../../../common/error-dialog';
import { minimumAgeValidator } from '../../../common/validators';
import { paymentFrequencyName, calculatePlanRate, calculatePlanRateWithDiscount } from '../../../common/helpers';
import { BillingTypes } from '../../../common/enums';
import { TurnstileTokenService } from '../../../services/turnstile-token.service';



@Component({
  selector: 'app-member-form',
   imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatDatepickerModule,
    MatCheckboxModule, MatInputModule, NgxMaskDirective, MatSelectModule, MatAutocompleteModule, 
    MatIconModule, DateMaskDirective, PlansSummaryComponent
  ],
  host:{
    class: 'w-full'
  },
  templateUrl: './member-form.component.html',
  styleUrl: './member-form.component.scss'
})
export class MemberFormComponent implements OnInit, OnChanges {
  @Input({required:true}) companyCode:string;
  @Input({required:true}) products:MasterClientProductViewDto[];
  @Input({required:true}) defaultPaymentFrequency: number;
  @Input() selectedProducts: number[] = [];
  @Input() discount: number = 0;
  @Input() existingData?: EnrollmentDraft; // For pre-populating form when editing
  @Input() billingType: number;
  @Input() requireEmployeeId: boolean = false;
  /** NH requires hire date on the draft (payroll and direct bill). */
  @Input() enrollmentType: EnrollmentType | null = null;
  @Output() continue = new EventEmitter<EnrollmentDraft>();

  get isPayrollDeducted(): boolean {
    return this.billingType === BillingTypes.PayrollDeducted;
  }

  get isNewHireEnrollment(): boolean {
    return this.enrollmentType === 'NH';
  }

  form: FormGroup;
  readonly states = States;
  filteredStates = new BehaviorSubject<KeyValue<string, string>[]>(States);
  readonly relationships = Relationships;
  readonly maxDate = new Date();
  termsExpanded = false;
  submitted = false;
  
  get minDate() {
    const mDate = new Date();
    mDate.setFullYear(mDate.getFullYear() - 125);
    return mDate;
  }

  get homeAddressCtrl(){
    return this.form.get('homeAddress').get('state');
  }

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private turnstileTokenService: TurnstileTokenService
  ){
    afterRenderEffect(() => {
      if (!this.turnstileTokenService.resetRequested() || !this.isPayrollDeducted) {
        return;
      }
      try {
        this.turnstileTokenService.reset();
      } catch {
        this.renderTurnstileWidget();
      }
    });
  }

  ngOnInit(): void {
    const primaryEmailValidators = this.isPayrollDeducted
      ? [Validators.email]
      : [Validators.required, Validators.email];

    this.form = this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      primaryEmail: ['', primaryEmailValidators],
      dateOfBirth: ['', Validators.required],
      mobilePhone: [''],
      homeAddress: this.createAddressGroup(),
      dependents: this.fb.array([]),
      companyCode: [this.companyCode],
      selectedProducts: [[this.selectedProducts]],
      paymentFrequency: [this.defaultPaymentFrequency],
      landingPageId: ['']
    });

    if (this.isPayrollDeducted) {
      const employeeIdValidators = this.requireEmployeeId
        ? [Validators.required, Validators.maxLength(50)]
        : [Validators.maxLength(50)];
      this.form.addControl('secondaryEmail', this.fb.control('', [Validators.required, Validators.email]));
      this.form.addControl('employeeId', this.fb.control('', employeeIdValidators));
      this.form.addControl('acceptTerms', this.fb.control(false, Validators.requiredTrue));
      this.initializeTurnstile();
    }

    this.syncHireDateControl();

    // Pre-populate form if editing existing data
    if (this.existingData) {
      const patchValue: any = {
        firstName: this.existingData.firstName,
        middleName: this.existingData.middleName,
        lastName: this.existingData.lastName,
        primaryEmail: this.existingData.primaryEmail,
        dateOfBirth: this.existingData.dateOfBirth,
        mobilePhone: this.existingData.mobilePhone,
        homeAddress: this.existingData.homeAddress,
        companyCode: this.existingData.companyCode,
        paymentFrequency: this.existingData.paymentFrequency
      };
      if (this.isPayrollDeducted && this.existingData.secondaryEmail !== undefined) {
        patchValue.secondaryEmail = this.existingData.secondaryEmail;
      }
      if (this.isPayrollDeducted && this.existingData.employeeId !== undefined) {
        patchValue.employeeId = this.existingData.employeeId;
      }
      if (this.existingData.hireDate !== undefined && this.form.get('hireDate')) {
        patchValue.hireDate = this.existingData.hireDate;
      }
      this.form.patchValue(patchValue);

      // Populate dependents if they exist
      if (this.existingData.dependents && this.existingData.dependents.length > 0) {
        this.existingData.dependents.forEach(dep => {
          const dependentGroup = this.fb.group({
            firstName: [dep.firstName, Validators.required],
            middleName: [dep.middleName],
            lastName: [dep.lastName, Validators.required],
            dateOfBirth: [dep.dateOfBirth, Validators.required],
            relationship: [dep.relationship, Validators.required]
          });
          this.dependents.push(dependentGroup);
        });
      }
    }

    this.products = this.products.filter(p => this.selectedProducts.includes(p.clientProductId));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) {
      return;
    }
    if (changes['enrollmentType'] || changes['billingType']) {
      this.syncHireDateControl();
    }
  }

  /** The control only exists for NH, so OE never shows or submits a hire date. */
  private syncHireDateControl(): void {
    if (!this.isNewHireEnrollment) {
      if (this.form.get('hireDate')) {
        this.form.removeControl('hireDate');
      }
      return;
    }
    if (!this.form.get('hireDate')) {
      this.form.addControl('hireDate', this.fb.control('', Validators.required));
    }
  }

  createAddressGroup() {
    return this.fb.group({
      address: ['', Validators.required],
      zipCode: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required]
    });
  }


  get dependents() {
    return this.form.get('dependents') as FormArray;
  }

  addDependent() {
    this.dependents.push(this.fb.group({
      firstName: ['', Validators.required],
      middleName: [''],
      lastName: ['', Validators.required],
      dateOfBirth: ['', Validators.required],
      relationship: ['', Validators.required]
    }));
  }

  removeDependent(index: number) {
    this.dependents.removeAt(index);
  }


  stateDisplay = (stateKey: string | null) => {
    if (stateKey == null || stateKey === '') return '';
    const hit = this.states.find((x) => x.key === stateKey);
    if (hit) return `${hit.key} – ${hit.value}`;
    return stateKey;
  };

  filterStates(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    const nextValue = filterValue
      ? this.states.filter(
          (o) =>
            o.key.toLowerCase().includes(filterValue) ||
            o.value.toLowerCase().includes(filterValue)
        )
      : this.states;

    this.filteredStates.next(nextValue);
  }

  onStateFocus(event: FocusEvent) {
    const v = this.homeAddressCtrl?.value;
    if (typeof v === 'string' && this.states.some((s) => s.key === v)) {
      // Valid selection: show full list (input still displays via displayWith).
      this.filteredStates.next(this.states);
    } else {
      this.filterStates(event);
    }
  }

  togglePlans(planId: number) {
    const selectedProducts = this.form.value.selectedProducts as number[];
    if (selectedProducts.includes(planId)) {
      this.form.patchValue({
        selectedProducts: selectedProducts.filter(id => id !== planId)
      });
    } else {
      this.form.patchValue({
        selectedProducts: [...selectedProducts, planId]
      });
    }
  }

  planRate(plan: MasterClientProductViewDto){
    return calculatePlanRate([plan], this.defaultPaymentFrequency ?? 1);
  }

  planRateWithDiscount(plan: MasterClientProductViewDto) {
    return calculatePlanRateWithDiscount([plan], this.defaultPaymentFrequency ?? 1, this.discount);
  }

  totalPrice() {
    return calculatePlanRate(this.products, this.defaultPaymentFrequency ?? 1);
  }

  totalPriceWithDiscount() {
    return calculatePlanRateWithDiscount(this.products, this.defaultPaymentFrequency ?? 1, this.discount);
  }
  
  paymentFrequency(){
    return paymentFrequencyName(this.defaultPaymentFrequency ?? 1)
  }

  toggleTerms() {
    this.termsExpanded = !this.termsExpanded;
  }

  private initializeTurnstile() {
    (window as any).onTurnstileSuccess = (token: string) => {
      this.turnstileTokenService.updateToken(token);
    }
    (window as any).onTurnstileError = () => {
      this.turnstileTokenService.updateToken(null);
    }
    (window as any).onTurnstileExpired = () => {
      this.turnstileTokenService.updateToken(null);
    }

    setTimeout(() => this.renderTurnstileWidget(), 100);
  }

  private renderTurnstileWidget() {
    const container = document.getElementById('turnstile-member-form');
    const turnstile = (window as any).turnstile;
    if (!container || !turnstile?.render) {
      return;
    }
    container.innerHTML = '';
    const widgetId = turnstile.render('#turnstile-member-form', {
      sitekey: '0x4AAAAAABDQhK0UT12Ulx-d',
      callback: (window as any).onTurnstileSuccess,
      'expired-callback': (window as any).onTurnstileExpired,
      'error-callback': (window as any).onTurnstileError,
      theme: 'light'
    });
    this.turnstileTokenService.setWidgetId(widgetId);
  }

  onSubmit() {
    this.submitted = true;
    //validate date of birth less than 18 years ago
    const dob = this.form.get('dateOfBirth').value;
    if (dob) {
      const today = new Date();
      const birthDate = new Date(dob);
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();
      if (age < 16 || (age === 16 && (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)))) {
        this.form.get('dateOfBirth').setErrors({ underage: true });
        this.dialog.open(ErrorDialogComponent, {
          data: { message: 'You must be at least 16 years old to enroll.' }
        });
        return;
      }
    }
    
    if (this.form.invalid) {
      this.markAllAsTouched(this.form);
      this.dialog.open(ErrorDialogComponent, {
        data: { message: 'Please fill in all required fields correctly.' }
      });
      return;
    }

    // Proceed with form submission
    let request = FormAsType<EnrollmentDraft>(this.form);
    this.continue.emit(request);
  }

  private markAllAsTouched(formGroup: FormGroup | FormArray) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }
}
